/*
 * Pointer input — tap on the Pixi stage to move the player toward that point.
 *
 * The DOM container hosting the Pixi canvas owns the events (not the canvas
 * directly) so we can disable while a modal is open without touching Pixi state.
 * Conversion from CSS coordinates to world coordinates is just a ratio because
 * the canvas is letterboxed by CSS to fit the container.
 */

import { WORLD } from '@/render/palette';
import { fireFirstInput } from '@/input/keys';

export interface PointerOptions {
  /** The element to listen on (usually the .pixi-stage div). */
  target: HTMLElement;
  /** Called with a world-space point to move the player toward. */
  onMove: (wx: number, wy: number) => void;
  /** Called to ask "should this tap be ignored right now?" (e.g. during fishing nibble). */
  shouldIgnore?: () => boolean;
}

export function initPointer(opts: PointerOptions): void {
  opts.target.addEventListener('pointerdown', (e) => {
    if (opts.shouldIgnore?.()) return;
    const rect = opts.target.getBoundingClientRect();
    const cssW = Math.max(1, rect.width);
    const cssH = Math.max(1, rect.height);
    const wx = ((e.clientX - rect.left) / cssW) * WORLD.width;
    const wy = ((e.clientY - rect.top) / cssH) * WORLD.height;
    const clampedX = Math.max(40, Math.min(WORLD.width - 40, wx));
    const clampedY = Math.max(150, Math.min(WORLD.height - 80, wy));
    opts.onMove(clampedX, clampedY);
    fireFirstInput();
  });
}

/** CSS → world ratio for positioning HTML overlays (well prompt) over a world coord. */
export function cssScaleOf(target: HTMLElement): { sx: number; sy: number } {
  const rect = target.getBoundingClientRect();
  return {
    sx: rect.width / WORLD.width,
    sy: rect.height / WORLD.height,
  };
}
